import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import * as React from "react";
import { create } from "zustand/index";
import { persist } from "zustand/middleware";

import { LoadSchematic } from "./actions/LoadSchematic";
import { SaveSchematic } from "./actions/SaveSchematic";
import { ScaffoldToZip } from "./actions/ScaffoldToZip";
import { configScaffolder } from "./scaffolding/configScaffolder";
import { Scaffolder } from "./scaffolding/scaffold";
import { scaffoldModels } from "./scaffolding/scaffolders/scaffoldModels";
import { scaffoldSchemas } from "./scaffolding/scaffolders/scaffoldSchemas";
import { scaffoldSerializer } from "./scaffolding/scaffolders/scaffoldSerializer";
import { scaffoldValidator } from "./scaffolding/scaffolders/scaffoldValidator";
import { createSchematic, moveSchematic, Schematic } from "./Schematic";
import {
  DraggedSchematic,
  SchematicBuilderContext,
} from "./SchematicBuilderContext";
import { ClassBuilder } from "./schematicBuilders/ClassBuilder";
import {
  classSchematicKind,
  defaultSchematicKinds,
  SchematicKind,
} from "./SchematicKind";

export interface SchematicBuilderProps {
  name?: string;
  title?: React.ReactNode;
  basePath?: string;
  basePackage?: string;
  baseDir?: string | ((schematic: Schematic) => string);
  defaultRootClassPackage?: string;
  defaultRootClassName?: string;
  schematicKinds?: SchematicKind[];
  scaffolders?:
    | Scaffolder<any>[]
    | ((schematic: Schematic) => Scaffolder<any>[]);
  actions?: React.ReactNode;
}

export function SchematicBuilder({
  name = "kform",
  title = document.title,
  basePath,
  basePackage,
  baseDir,
  defaultRootClassPackage = "org.example",
  defaultRootClassName = "MyForm",
  schematicKinds = defaultSchematicKinds,
  scaffolders = [
    configScaffolder(scaffoldSchemas, {
      currentDir: "src/commonMain/kotlin",
    }),
    configScaffolder(scaffoldModels, {
      currentDir: "src/commonMain/kotlin",
    }),
    configScaffolder(scaffoldSerializer, {
      currentDir: "src/commonMain/kotlin",
    }),
    configScaffolder(scaffoldValidator, {
      currentDir: "src/commonTest/kotlin",
    }),
  ],
  actions = (
    <>
      <LoadSchematic />
      <SaveSchematic />
      <ScaffoldToZip />
    </>
  ),
}: SchematicBuilderProps) {
  const [useSchematic] = React.useState(() =>
    create(
      persist(
        () =>
          createSchematic({
            kind: classSchematicKind.kind,
            packageSuffix: defaultRootClassPackage,
            name: defaultRootClassName,
            children: [],
          }),
        { name },
      ),
    ),
  );
  const schematicKindsMap = React.useMemo(
    () =>
      new Map(
        schematicKinds
          .sort((t1, t2) => t1.kind.localeCompare(t2.kind))
          .map((t) => [t.kind, t]),
      ),
    [schematicKinds],
  );
  const latestSchematicFileHandle = React.useRef<FileSystemFileHandle | null>(
    null,
  );

  const [draggedSchematic, setDraggedSchematic] =
    React.useState<DraggedSchematic | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10, delay: 400, tolerance: 20 },
    }),
    useSensor(KeyboardSensor),
  );

  return (
    <SchematicBuilderContext.Provider
      value={{
        name,
        basePath,
        basePackage,
        baseDir,
        schematicKinds: schematicKindsMap,
        scaffolders,
        getRootSchematic: useSchematic.getState,
        useSchematic: useSchematic as never,
        setSchematic: useSchematic.setState,
        removeSchematic: () => {},
        parentPackage: basePackage,
        draggedSchematic,
        latestSchematicFileHandle,
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(evt) =>
          setDraggedSchematic({
            id: evt.active.id as string,
            ...evt.active.data.current,
          } as DraggedSchematic)
        }
        onDragEnd={(evt) => {
          const { parentId, sortBefore, ignored } =
            evt.over?.data.current ?? {};
          if (!ignored) {
            useSchematic.setState((schematic) =>
              moveSchematic(
                schematic,
                evt.active.id as string,
                parentId,
                sortBefore,
              ),
            );
          }
          setDraggedSchematic(null);
        }}
      >
        <form className="builder-root">
          <header className="builder-header">
            <h1 className="builder-title">{title}</h1>
            <div className="builder-actions">{actions}</div>
          </header>

          <ClassBuilder />
        </form>

        <DragOverlay>
          {draggedSchematic && (
            <div className="builder-dragged">{draggedSchematic.node}</div>
          )}
        </DragOverlay>
      </DndContext>
    </SchematicBuilderContext.Provider>
  );
}
